import { useTranslation } from 'react-i18next';
import type { CallChannel, CallWindow } from '@/lib/api/types';
import { Eyebrow, MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { dayLabel, isValidZone, wallTime, zonedInstant } from './callTime';

interface Props {
  channel: CallChannel;
  windows: CallWindow[];
  visitorZone: string;
  phone: string;
}

/** Digits only, for a wa.me / tel: target. */
function dialable(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

/**
 * The call-request half of a contact lead: how the visitor wants to talk and the
 * windows they offered, in business time with the visitor's own clock beside it.
 * Sales confirms one window by reply — nothing here is reserved.
 */
export function CallRequestPanel({ channel, windows, visitorZone, phone }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';
  const businessZone = windows[0]?.timezone ?? '';
  // Only worth saying when the visitor's clock differs from the business zone.
  const showVisitor = !!visitorZone && visitorZone !== businessZone && isValidZone(visitorZone);
  const number = dialable(phone);
  const action =
    channel === 'whatsapp' && number
      ? { href: `https://wa.me/${number.replace(/^\+/, '')}`, label: t('leads.call.openWhatsApp') }
      : channel === 'phone' && number
        ? { href: `tel:${number}`, label: t('leads.call.dial') }
        : null;

  return (
    <section
      aria-label={t('leads.call.heading')}
      className="rounded-md border-[0.5px] border-hairline bg-canvas-bone p-md"
    >
      <div className="flex flex-wrap items-center justify-between gap-sm">
        <div className="flex items-center gap-sm">
          <Eyebrow>{t('leads.call.heading')}</Eyebrow>
          <Tag variant="teal">{t(`leads.call.channel.${channel}`)}</Tag>
        </div>
        {action && (
          <a
            href={action.href}
            target={channel === 'whatsapp' ? '_blank' : undefined}
            rel="noopener noreferrer"
            className="text-small font-medium text-accent-blue underline-offset-2 hover:underline"
          >
            {action.label}
          </a>
        )}
      </div>

      {windows.length === 0 ? (
        <p className="mt-sm text-small text-ink-mute">{t('leads.call.noWindows')}</p>
      ) : (
        <ol className="mt-sm flex flex-col gap-xs">
          {windows.map((w, i) => {
            const start = zonedInstant(w.date, w.start, w.timezone);
            const end = zonedInstant(w.date, w.end, w.timezone);
            const visitor = showVisitor
              ? `${wallTime(start, visitorZone, locale)}–${wallTime(end, visitorZone, locale)}`
              : null;
            return (
              <li key={`${w.date}-${w.window}`} className="flex flex-wrap items-baseline gap-x-sm text-small">
                <MonoValue className="text-ink-mute">{i + 1}.</MonoValue>
                <span className="font-medium text-ink-deep">{dayLabel(w.date, w.timezone, locale)}</span>
                <MonoValue className="text-ink-graphite">
                  {w.start}–{w.end}
                </MonoValue>
                {visitor && visitor !== `${w.start}–${w.end}` && (
                  <span className="text-ink-mute">{t('leads.call.visitorTime', { time: visitor })}</span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {windows.length > 0 && (
        <p className="mt-sm text-small text-ink-mute">
          {t('leads.call.zoneNote', { zone: businessZone })}
          {showVisitor ? ` ${t('leads.call.visitorZone', { zone: visitorZone })}` : ''}
        </p>
      )}
    </section>
  );
}
